import React, { useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useToggleContext } from '../../contexts/toggle';
import { FetchCurrentUserQuery } from '../../generated/hooks';
import { getIosOnboardingState } from '../../utils/pushClient';

const NEW_USER_CUTOFF = new Date('2026-01-01T00:00:00Z');
const DISMISS_KEY = 'welcomeDismissed:2026';

type WelcomePanelProps = {
  currentUser: FetchCurrentUserQuery['currentUser'];
};

// Push only works on iPhone from an installed home-screen app, never a Safari tab -- this is the
// one place every user (new or returning) is guaranteed to see that, so it's not duplicated into
// both banner variants below. Mirrors the same getIosOnboardingState() the /notifications page
// itself uses, so the two can't disagree.
function InstallNotificationsCallout() {
  const iosState = getIosOnboardingState();
  const setUpButton = (label: string) => (
    <Button component={Link} to="/notifications/preferences" color="inherit" size="small">
      {label}
    </Button>
  );

  if (iosState === 'ready') return null;
  if (iosState === 'not-ios') {
    return (
      <Alert severity="info" action={setUpButton('Set up')}>
        Turn on notifications to get picks, results, and trades in real time.
      </Alert>
    );
  }
  if (iosState === 'needs-permission') {
    return (
      <Alert severity="info" action={setUpButton('Turn on')}>
        🔔 You&apos;re almost set — turn on notifications.
      </Alert>
    );
  }
  // needs-install / needs-safari / unsupported-version
  return (
    <Alert severity="warning" action={setUpButton('Set up notifications')}>
      <strong>📲 Notifications only work once this is on your Home Screen.</strong> On iPhone, push notifications
      require installing the app first — it takes 10 seconds.
    </Alert>
  );
}

function WelcomePanel({ currentUser }: WelcomePanelProps) {
  const { getToggle } = useToggleContext();
  const allowLeagueCreation = getToggle('allowLeagueCreation')?.on || currentUser.isSuperuser;
  const isReturning = new Date(currentUser.dateJoined) < NEW_USER_CUTOFF;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');
  const firstName = currentUser.fullName.split(' ')[0];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
      <InstallNotificationsCallout />
      {!(isReturning && dismissed) && (
        <Paper sx={{ p: 2, position: 'relative' }}>
          {isReturning && (
            <IconButton
              size="small"
              aria-label="Dismiss"
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => {
                localStorage.setItem(DISMISS_KEY, '1');
                setDismissed(true);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          {isReturning ? (
            <>
              <Typography variant="h5" gutterBottom>
                Welcome back, {firstName}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                What&apos;s new this season:
              </Typography>
              <ul style={{ marginTop: 4, paddingLeft: '1.25em' }}>
                <li>
                  <strong>New: revamped Stock Market league</strong> — real orders and live trades between members.
                </li>
                <li>
                  <strong>Install as a mobile app</strong> — add College Pick Em to your phone&apos;s home screen for
                  one-tap access.
                </li>
              </ul>
            </>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>
                What is College Pick Em?
              </Typography>
              <Typography variant="body1" paragraph>
                A football pick-league site with two ways to play: <strong>Pick &apos;Em</strong>, where you pick a
                winner against the spread for 10 games each week, and <strong>Stock Market</strong>, where you trade
                shares of college football teams with other members in a live market and receive dividends based on team
                accomplishments.
              </Typography>
              <Typography variant="body1" gutterBottom>
                {allowLeagueCreation
                  ? 'Ask a friend to invite you to their league, or create your own below.'
                  : 'Ask a friend who runs a league to send you an invite to get started.'}
              </Typography>
              <Typography variant="body1">
                <strong>Tip:</strong> add College Pick Em to your phone&apos;s home screen for one-tap access (and push
                notifications on iPhone).
              </Typography>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default WelcomePanel;
